import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { debounce } from 'lodash';
import styles from './LogsTab.module.css';
import { logsApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import LogModal from './LogModal';
import InputModal from '../../../ui/inputModal/InputModal';
import Pagination from '../../../ui/pagination/Pagination';
import Checkbox from '../../../ui/Checkbox/Checkbox';
import Loader from '../../../ui/Loader/Loader';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { useAuthStore } from '../../../store/authStore';
import { UserRole } from '../../../enums/userRole';
import { Log, LogUserOption } from '../../../types/types';


interface LogsTabProps {
}

const LEVEL_TYPE_CLASS: Record<string, string> = {
    info: styles.logTypeInfo,
    warn: styles.logTypeWarn,
    error: styles.logTypeError,
    debug: styles.logTypeDebug,
    success: styles.logTypeSuccess,
};

const TOOLTIP_OFFSET = 16;
const TOOLTIP_MARGIN = 8;

const LogsTab: React.FC<LogsTabProps> = ({ }) => {
    const currentUser = useAuthStore(state => state.user);
    const isSuperAdmin = currentUser?.role?.id === UserRole.SuperAdmin;
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState<Log | null>(null);
    const [levelFilter, setLevelFilter] = useState('');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [dateFilter, setDateFilter] = useState('');
    const [fromTimeFilter, setFromTimeFilter] = useState('');
    const [toTimeFilter, setToTimeFilter] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [logs, setLogs] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [pageCount, setPageCount] = useState(1);
    const [page, setPage] = useState(1);
    const [levels, setLevels] = useState<string[]>([]);
    const [sources, setSources] = useState<string[]>([]);
    const [users, setUsers] = useState<LogUserOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showClearAllModal, setShowClearAllModal] = useState(false);
    const [deletingLogs, setDeletingLogs] = useState(false);
    const [clearingLogs, setClearingLogs] = useState(false);
    const [tooltipText, setTooltipText] = useState<string | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const mousePosRef = useRef({ x: 0, y: 0 });
    const hideTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const PAGE_SIZE = 50;

    const clearHideTooltipTimeout = () => {
        if (hideTooltipTimeoutRef.current) {
            clearTimeout(hideTooltipTimeoutRef.current);
            hideTooltipTimeoutRef.current = null;
        }
    };

    const positionTooltip = () => {
        const el = tooltipRef.current;
        if (!el) return;
        const { x, y } = mousePosRef.current;
        const rect = el.getBoundingClientRect();
        let left = x + TOOLTIP_OFFSET;
        let top = y + TOOLTIP_OFFSET;
        if (left + rect.width > window.innerWidth - TOOLTIP_MARGIN) {
            left = x - rect.width - TOOLTIP_OFFSET;
        }
        if (top + rect.height > window.innerHeight - TOOLTIP_MARGIN) {
            top = y - rect.height - TOOLTIP_OFFSET;
        }
        left = Math.max(TOOLTIP_MARGIN, left);
        top = Math.max(TOOLTIP_MARGIN, top);
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
    };

    const showTooltip = (text: string | null | undefined, e: React.MouseEvent) => {
        if (!text) return;
        clearHideTooltipTimeout();
        mousePosRef.current = { x: e.clientX, y: e.clientY };
        setTooltipText(text);
    };

    const moveTooltip = (e: React.MouseEvent) => {
        mousePosRef.current = { x: e.clientX, y: e.clientY };
        positionTooltip();
    };

    const scheduleHideTooltip = () => {
        clearHideTooltipTimeout();
        hideTooltipTimeoutRef.current = setTimeout(() => setTooltipText(null), 150);
    };

    useLayoutEffect(() => {
        if (tooltipText) {
            positionTooltip();
        }
    }, [tooltipText]);

    useEffect(() => {
        const handler = debounce((value) => {
            setDebouncedSearch(value);
            setPage(1);
        }, 1000);
        handler(search);
        return () => { handler.cancel(); };
    }, [search]);

    useEffect(() => {
        fetchLogs();
    }, [debouncedSearch, page, levelFilter, dateFilter, fromTimeFilter, toTimeFilter, sourceFilter, userFilter]);

    const fetchLogs = async () => {
        setLoading(true);
        const logsData = logsApi.get
            ? await logsApi.get(page, PAGE_SIZE, levelFilter, debouncedSearch, dateFilter, sourceFilter, fromTimeFilter, toTimeFilter, userFilter)
            : Promise.resolve([]);
        setLogs(logsData.logs || []);
        setLevels(logsData.levels || []);
        setSources(logsData.sources || []);
        setUsers(logsData.users || []);
        setPageCount(Math.ceil(logsData.totalLogs / PAGE_SIZE));
        setSelectedIds([]);
        setLoading(false);
    };

    const handleSelect = (id: number, checked: boolean) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(i => i !== id));
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (deletingLogs) return;
        try {
            setDeletingLogs(true);
            await logsApi.bulkDelete(selectedIds);
            toast.success('Logs deleted successfully!');
            fetchLogs();
        } catch (err) {
            toast.error('Bulk delete failed');
        } finally {
            setDeletingLogs(false);
            setShowDeleteModal(false);
        }
    };

    const handleClearAll = () => {
        setShowClearAllModal(true);
    };

    const handleConfirmClearAll = async () => {
        if (clearingLogs) return;
        try {
            setClearingLogs(true);
            await logsApi.clearAll();
            toast.success('All logs cleared!');
            fetchLogs();
        } catch (err) {
            toast.error('Failed to clear all logs');
        } finally {
            setClearingLogs(false);
            setShowClearAllModal(false);
        }
    };

    const handleSeeMore = (log: Log) => {
        setSelectedLog(log);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedLog(null);
    };

    const handleLevelChange = async (newLevel: string) => {
        setLevelFilter(newLevel);
        setLoading(true);
        try {
            const logsData = await logsApi.get(page, PAGE_SIZE, newLevel, debouncedSearch, dateFilter, sourceFilter, fromTimeFilter, toTimeFilter, userFilter);
            setLogs(logsData.logs || []);
            setLevels(logsData.levels || []);
            setSources(logsData.sources || []);
            setUsers(logsData.users || []);
            setPageCount(Math.ceil(logsData.totalLogs / PAGE_SIZE));
        } catch (error) {
            toast.error('Failed to fetch logs for the selected level.');
        } finally {
            setLoading(false);
        }
    };

    const handleSourceChange = async (newSource: string) => {
        setSourceFilter(newSource);
        setLoading(true);
        try {
            const logsData = await logsApi.get(page, PAGE_SIZE, levelFilter, debouncedSearch, dateFilter, newSource, fromTimeFilter, toTimeFilter, userFilter);
            setLogs(logsData.logs || []);
            setLevels(logsData.levels || []);
            setSources(logsData.sources || []);
            setUsers(logsData.users || []);
            setPageCount(Math.ceil(logsData.totalLogs / PAGE_SIZE));
        } catch (error) {
            toast.error('Failed to fetch logs for the selected source.');
        } finally {
            setLoading(false);
        }
    };

    const handleUserChange = async (newUser: string) => {
        setUserFilter(newUser);
        setLoading(true);
        try {
            const logsData = await logsApi.get(page, PAGE_SIZE, levelFilter, debouncedSearch, dateFilter, sourceFilter, fromTimeFilter, toTimeFilter, newUser);
            setLogs(logsData.logs || []);
            setLevels(logsData.levels || []);
            setSources(logsData.sources || []);
            setUsers(logsData.users || []);
            setPageCount(Math.ceil(logsData.totalLogs / PAGE_SIZE));
        } catch (error) {
            toast.error('Failed to fetch logs for the selected user.');
        } finally {
            setLoading(false);
        }
    };

    const escapeCsvValue = (value: string) => {
        if (/[",\n]/.test(value)) {
            return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
    };

    const handleExportSelected = () => {
        if (selectedIds.length === 0) return;
        const selectedLogs = logs.filter(log => selectedIds.includes(log.id));
        const header = ['ID', 'Timestamp', 'Level', 'Source', 'User', 'Message', 'Context', 'Visibility'];
        const rows = selectedLogs.map(log => [
            log.id ?? '',
            new Date(log.timestamp).toLocaleString(),
            log.level ?? '',
            log.source ?? '',
            log.user_email ?? log.user_id ?? '',
            log.message ?? '',
            log.context ?? '',
            log.min_visibility_role_id === UserRole.SuperAdmin ? 'SuperAdmin only' : '',
        ]);
        const csv = [header, ...rows]
            .map(row => row.map(cell => escapeCsvValue(String(cell))).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs-export-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${selectedLogs.length} log(s)`);
    };

    const hasActiveFilters = !!(levelFilter || sourceFilter || userFilter || search || dateFilter || fromTimeFilter || toTimeFilter);

    const handleResetFilters = () => {
        setLevelFilter('');
        setSourceFilter('');
        setUserFilter('');
        setSearch('');
        setDebouncedSearch('');
        setDateFilter('');
        setFromTimeFilter('');
        setToTimeFilter('');
        setPage(1);
    };

    return (
        <div className={styles.logsTableWrapper}>
            {loading ? (
                <Loader />
            ) : (
                <>
                    <div className={styles.eventLog}>
                        {/* Header */}
                        <div className={styles.logHeader}>
                            <h3>System Logs</h3>
                            <div className={styles.logActions}>
                                <button className={styles.logBtn} onClick={() => fetchLogs()}>
                                    ↻ Refresh
                                </button>
                                {isSuperAdmin && (
                                    <>
                                        <button className={styles.logBtn} onClick={handleClearAll}>
                                            Clear All
                                        </button>
                                        <button
                                            className={styles.logBtn}
                                            onClick={handleBulkDelete}
                                            disabled={selectedIds.length === 0}
                                        >
                                            Delete ({selectedIds.length})
                                        </button>
                                        <button
                                            className={styles.logBtn}
                                            onClick={handleExportSelected}
                                            disabled={selectedIds.length === 0}
                                        >
                                            Export ({selectedIds.length})
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Filters */}
                        <div className={styles.logFilters}>
                            <div>
                                <button
                                    className={`${styles.logFilter} ${levelFilter === '' ? styles.logFilterActive : ''}`}
                                    onClick={() => handleLevelChange('')}
                                >
                                    all
                                </button>
                                {levels.map(level => (
                                    <button
                                        key={level}
                                        className={`${styles.logFilter} ${levelFilter === level
                                            ? level === 'error' ? styles.logFilterErrorActive : styles.logFilterActive
                                            : ''}`}
                                        onClick={() => handleLevelChange(level)}
                                    >
                                        {level}
                                    </button>
                                ))}
                            </div>
                            <select
                                className={styles.sourceSelect}
                                value={sourceFilter}
                                onChange={e => handleSourceChange(e.target.value)}
                            >
                                <option value="">All sources</option>
                                {sources.map(source => (
                                    <option key={source} value={source}>{source}</option>
                                ))}
                            </select>
                            <select
                                className={styles.sourceSelect}
                                value={userFilter}
                                onChange={e => handleUserChange(e.target.value)}
                            >
                                <option value="">All users</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.email || u.id}</option>
                                ))}
                            </select>
                            <div className={styles.logFilterSpacer} />
                            <div className={styles.dateTimeFilterGroup}>
                                <SecureInput
                                    fieldType="date"
                                    value={dateFilter}
                                    onChange={value => setDateFilter(value)}
                                    className={`${styles.filterDate} ${styles.dateTimeFilterItem}`}
                                />
                                <span className={styles.dateTimeFilterSeparator} />
                                <SecureInput
                                    fieldType="time"
                                    value={fromTimeFilter}
                                    onChange={value => setFromTimeFilter(value)}
                                    className={`${styles.filterDate} ${styles.dateTimeFilterItem} ${styles.dateTimeFilterTime}`}
                                />
                                <span className={styles.dateTimeFilterArrow} aria-hidden="true">→</span>
                                <SecureInput
                                    fieldType="time"
                                    value={toTimeFilter}
                                    onChange={value => setToTimeFilter(value)}
                                    className={`${styles.filterDate} ${styles.dateTimeFilterItem} ${styles.dateTimeFilterTime}`}
                                />
                            </div>
                            <SecureInput
                                fieldType="text"
                                value={search}
                                onChange={value => setSearch(value)}
                                placeholder="Search..."
                                className={styles.filterSearch}
                            />
                            <button
                                className={styles.filterResetBtn}
                                onClick={handleResetFilters}
                                disabled={!hasActiveFilters}
                            >
                                Reset
                            </button>
                        </div>

                        {/* Log entries */}
                        <div className={styles.logEntries}>
                            {logs.length === 0 ? (
                                <div className={styles.noLogs}>No logs found.</div>
                            ) : (
                                <>
                                    <div className={styles.logTableHeader}>
                                        <span />
                                        <span>Time</span>
                                        <span>Level</span>
                                        <span>Source</span>
                                        <span>User</span>
                                        <span>Message</span>
                                        <span>Context</span>
                                    </div>
                                    {logs.map(log => (
                                        <div
                                            key={log.id}
                                            className={styles.logEntry}
                                            onClick={() => handleSeeMore(log)}
                                        >
                                            {/* Desktop / wide-screen view */}
                                            <div className={styles.logEntryDesktop}>
                                                {isSuperAdmin && (
                                                    <span
                                                        className={styles.logCheckbox}
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <Checkbox
                                                            checked={selectedIds.includes(log.id)}
                                                            onChange={e => handleSelect(log.id, e)}
                                                        />
                                                    </span>
                                                )}
                                                <span
                                                    className={styles.logTime}
                                                    onMouseEnter={e => showTooltip(new Date(log.timestamp).toLocaleString(), e)}
                                                    onMouseMove={moveTooltip}
                                                    onMouseLeave={scheduleHideTooltip}
                                                >
                                                    {new Date(log.timestamp).toLocaleString()}
                                                </span>
                                                <span
                                                    className={`${styles.logType} ${LEVEL_TYPE_CLASS[log.level] || ''}`}
                                                    onMouseEnter={e => showTooltip(log.level, e)}
                                                    onMouseMove={moveTooltip}
                                                    onMouseLeave={scheduleHideTooltip}
                                                >
                                                    [{log.level}]
                                                </span>
                                                {log.source && (
                                                    <span
                                                        className={styles.logSource}
                                                        onMouseEnter={e => showTooltip(log.source, e)}
                                                        onMouseMove={moveTooltip}
                                                        onMouseLeave={scheduleHideTooltip}
                                                    >
                                                        [{log.source}]
                                                    </span>
                                                )}
                                                {isSuperAdmin && log.min_visibility_role_id === UserRole.SuperAdmin && (
                                                    <span
                                                        className={styles.logVisibilityBadge}
                                                        onMouseEnter={e => showTooltip('Only visible to SuperAdmins', e)}
                                                        onMouseMove={moveTooltip}
                                                        onMouseLeave={scheduleHideTooltip}
                                                    >
                                                        SuperAdmin only
                                                    </span>
                                                )}
                                                {(log.user_email || log.user_id) && (
                                                    <span
                                                        className={styles.logUser}
                                                        onMouseEnter={e => showTooltip(log.user_email || log.user_id, e)}
                                                        onMouseMove={moveTooltip}
                                                        onMouseLeave={scheduleHideTooltip}
                                                    >
                                                        {log.user_email || log.user_id}
                                                    </span>
                                                )}
                                                <span
                                                    className={log.level === 'error' ? styles.logMsgError : styles.logMsg}
                                                    onMouseEnter={e => showTooltip(log.message, e)}
                                                    onMouseMove={moveTooltip}
                                                    onMouseLeave={scheduleHideTooltip}
                                                >
                                                    <span className={styles.ellipsisInner}>{log.message}</span>
                                                </span>
                                                {log.context && (
                                                    <span
                                                        className={styles.logContextInline}
                                                        onMouseEnter={e => showTooltip(log.context, e)}
                                                        onMouseMove={moveTooltip}
                                                        onMouseLeave={scheduleHideTooltip}
                                                    >
                                                        <span className={styles.ellipsisInner}>{log.context}</span>
                                                    </span>
                                                )}
                                            </div>

                                            {/* Small-screen table view */}
                                            <div className={styles.logEntryMobile}>
                                                <span
                                                    className={styles.logMobileCheckbox}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    {isSuperAdmin && (
                                                        <Checkbox
                                                            checked={selectedIds.includes(log.id)}
                                                            onChange={e => handleSelect(log.id, e)}
                                                        />
                                                    )}
                                                </span>
                                                <span
                                                    className={styles.logMobileCell}
                                                    onMouseEnter={e => showTooltip(new Date(log.timestamp).toLocaleString(), e)}
                                                    onMouseMove={moveTooltip}
                                                    onMouseLeave={scheduleHideTooltip}
                                                >
                                                    <span className={styles.ellipsisInner}>{new Date(log.timestamp).toLocaleString()}</span>
                                                </span>
                                                <span
                                                    className={`${styles.logMobileCell} ${LEVEL_TYPE_CLASS[log.level] || ''}`}
                                                    onMouseEnter={e => showTooltip(log.level, e)}
                                                    onMouseMove={moveTooltip}
                                                    onMouseLeave={scheduleHideTooltip}
                                                >
                                                    <span className={styles.ellipsisInner}>{log.level}</span>
                                                </span>
                                                <span
                                                    className={styles.logMobileCell}
                                                    onMouseEnter={e => showTooltip(log.source, e)}
                                                    onMouseMove={moveTooltip}
                                                    onMouseLeave={scheduleHideTooltip}
                                                >
                                                    <span className={styles.ellipsisInner}>{log.source || '-'}</span>
                                                </span>
                                                <span
                                                    className={styles.logMobileCell}
                                                    onMouseEnter={e => showTooltip(log.user_email || log.user_id, e)}
                                                    onMouseMove={moveTooltip}
                                                    onMouseLeave={scheduleHideTooltip}
                                                >
                                                    <span className={styles.ellipsisInner}>{log.user_email || log.user_id || '-'}</span>
                                                </span>
                                                <span
                                                    className={`${styles.logMobileCell} ${log.level === 'error' ? styles.logMsgError : ''}`}
                                                    onMouseEnter={e => showTooltip(log.message, e)}
                                                    onMouseMove={moveTooltip}
                                                    onMouseLeave={scheduleHideTooltip}
                                                >
                                                    <span className={styles.ellipsisInner}>{log.message}</span>
                                                </span>
                                                <span
                                                    className={styles.logMobileCell}
                                                    onMouseEnter={e => showTooltip(log.context, e)}
                                                    onMouseMove={moveTooltip}
                                                    onMouseLeave={scheduleHideTooltip}
                                                >
                                                    <span className={styles.ellipsisInner}>{log.context || '-'}</span>
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>

                    <Pagination page={page} pageCount={pageCount} setPage={setPage} className={styles.pagination} />

                    {isSuperAdmin && (
                        <>
                            <InputModal
                                open={showDeleteModal}
                                title="Delete Logs"
                                confirmText="Delete"
                                cancelText="Cancel"
                                showInput={false}
                                message={`Are you sure you want to delete ${selectedIds.length} logs? This action cannot be undone.`}
                                loading={deletingLogs}
                                loadingText="Deleting..."
                                onConfirm={handleConfirmDelete}
                                onCancel={() => {
                                    if (!deletingLogs) {
                                        setShowDeleteModal(false);
                                    }
                                }}
                            />
                            <InputModal
                                open={showClearAllModal}
                                title="Clear All Logs"
                                confirmText="Clear All"
                                cancelText="Cancel"
                                showInput={false}
                                danger={true}
                                message="You are about to permanently delete every log in the system. This cannot be undone."
                                loading={clearingLogs}
                                loadingText="Clearing..."
                                onConfirm={handleConfirmClearAll}
                                onCancel={() => {
                                    if (!clearingLogs) {
                                        setShowClearAllModal(false);
                                    }
                                }}
                            />
                        </>
                    )}
                    <LogModal open={modalOpen} onClose={handleCloseModal} log={selectedLog} />
                </>
            )}
            {tooltipText && createPortal(
                <div
                    ref={tooltipRef}
                    className={styles.logTooltip}
                    onMouseEnter={clearHideTooltipTimeout}
                    onMouseLeave={scheduleHideTooltip}
                >
                    {tooltipText}
                </div>,
                document.body
            )}
        </div>
    );
};

export default LogsTab;
